import React from 'react';
import PropTypes from 'prop-types';
import useUserContext from 'Context/UserContext';
import {
  MemberEditMessageInput,
} from 'Components/ComponentLibrary/Components/Uncategorized/EditMessageInput/components/MemberEditMessageInput';
import {
  AgentEditMessageInput,
} from 'Components/ComponentLibrary/Components/Uncategorized/EditMessageInput/components/AgentEditMessageInput';
import { noop } from 'Helpers/PropTypes';
import fr from 'facet-rest';
import { useMessageInput } from 'Components/ComponentLibrary/Components/Uncategorized/MessageInput';
import { buildUseStyles, useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import classNames from 'classnames';


export const DEFAULT_INPUT_MIN_HEIGHT_FOR_MESSAGE_EDIT = 80;
export const AUTOGROW_MAX_HEIGHT_FOR_MESSAGE_EDIT = 400;
// Padding correction is needed to that there is not a jump in editor size once the content loads and the user
// clicks to edit the content.
export const TEXT_EDITOR_PADDING_CORRECTION_FOR_MESSAGE_EDIT = 18;

const useStyles = buildUseStyles('EditMessageInput', (theme) => ({
  root: {
    paddingRight: theme.spacing(2),
  },
}));

const EditMessageInput = ({
  className,
  messageResource,
  onClose,
  rounded,
  inputMinHeight,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const [text, setText] = React.useState(messageResource.attr('content'));
  const { isAgent } = useUserContext();
  const EditMessageInputComponent = isAgent ? AgentEditMessageInput : MemberEditMessageInput;
  const heightForCkEditor = inputMinHeight < DEFAULT_INPUT_MIN_HEIGHT_FOR_MESSAGE_EDIT
    ? DEFAULT_INPUT_MIN_HEIGHT_FOR_MESSAGE_EDIT
    : inputMinHeight - TEXT_EDITOR_PADDING_CORRECTION_FOR_MESSAGE_EDIT;

  const sendMessage = async () => {
    await messageResource
      .attr('content', text)
      .submit();
  };

  const {
    handleSubmit,
  } = useMessageInput(
    messageResource,
    sendMessage,
  );

  const handleChange = (value) => {
    setText(value);
  };

  const onSubmit = async () => {
    await handleSubmit(text);
    onClose();
  };

  return (
    <EditMessageInputComponent
      className={classNames(className, classes.root)}
      onClose={onClose}
      handleChange={handleChange}
      handleSubmit={onSubmit}
      value={text}
      rounded={rounded}
      inputMinHeight={DEFAULT_INPUT_MIN_HEIGHT_FOR_MESSAGE_EDIT}
      customConfig={{
        // The height is the initial height of the editor when the content the user can edit loads.
        height: heightForCkEditor,
        // The edit box is not able to get any smaller than autogrow min height.
        autoGrow_minHeight: DEFAULT_INPUT_MIN_HEIGHT_FOR_MESSAGE_EDIT,
        // Autogrow max height is the maximum height the edit box will go to when the user hits enter
        // before it starts scrolling.
        autoGrow_maxHeight: AUTOGROW_MAX_HEIGHT_FOR_MESSAGE_EDIT,
      }}
    />
  );
};

EditMessageInput.defaultProps = {
  className: '',
  onClose: noop,
  rounded: false,
  inputMinHeight: DEFAULT_INPUT_MIN_HEIGHT_FOR_MESSAGE_EDIT,
};

EditMessageInput.propTypes = {
  className: PropTypes.string,
  messageResource: PropTypes.instanceOf(fr.Resource).isRequired,
  onClose: PropTypes.func,
  rounded: PropTypes.bool,
  inputMinHeight: PropTypes.number,
};

export { EditMessageInput };
